import { apiClient } from '@/lib/api-client';
import type { Shift, Attendance } from '@bbpos/types';

// ─── Request / Response types ────────���───────────────────────────────────────

interface ShiftListFilters {
  location_id?: string;
  is_active?: boolean;
  page?: number;
  limit?: number;
}

interface AttendanceListFilters {
  location_id?: string;
  user_id?: string;
  shift_id?: string;
  start_date?: string;
  end_date?: string;
  status?: string;
  page?: number;
  limit?: number;
}

interface ClockInDto {
  shift_id: string;
  location_id: string;
  notes?: string;
  /** Opening cash float, minor units. */
  opening_float?: number;
}

interface ClockOutDto {
  attendance_id: string;
  notes?: string;
}

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// ─── Service ───��─────────────────────────────────────────────────────────────

/** A parked bill at the store, listed as a clock-out warning. */
export interface ParkedBillWarning {
  id: string;
  park_reference: string | null;
  parked_at: string | null;
  parked_by_name: string | null;
}

export interface ClockOutCheck {
  can_clock_out: boolean;
  warnings: { parked_bills: ParkedBillWarning[] };
}

export const shiftsService = {
  getShifts(filters: ShiftListFilters = {}) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null) {
        params.set(key, String(value));
      }
    }
    const qs = params.toString();
    return apiClient<{ data: Shift[]; meta: PaginationMeta }>(
      `/shifts${qs ? `?${qs}` : ''}`,
    );
  },

  getShift(id: string) {
    return apiClient<Shift>(`/shifts/${id}`);
  },

  clockIn(dto: ClockInDto) {
    return apiClient<Attendance>('/attendance/clock-in', {
      method: 'POST',
      body: dto,
    });
  },

  clockOut(dto: ClockOutDto) {
    return apiClient<Attendance>('/attendance/clock-out', {
      method: 'POST',
      body: dto,
    });
  },

  getAttendance(filters: AttendanceListFilters = {}) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null) {
        params.set(key, String(value));
      }
    }
    const qs = params.toString();
    return apiClient<{ data: Attendance[]; meta: PaginationMeta }>(
      `/attendance${qs ? `?${qs}` : ''}`,
    );
  },

  getActiveAttendance() {
    return apiClient<Attendance | null>('/attendance/active');
  },

  /**
   * What to warn about before clock-out (gst-parked 110, D3): the store's
   * parked bills — every till's. They no longer block clock-out.
   */
  getClockOutBlockers(attendanceId: string) {
    return apiClient<ClockOutCheck>(`/attendance/${attendanceId}/clock-out-blockers`);
  },
};
